/**
 * Équipement (phase C) : applique un chargement aux réglages d'une session.
 *
 * Le résultat est une copie des réglages où le profil du châssis, les armes, la jauge de boost et le radar
 * portent les effets des objets : la simulation, le HUD et le pilote automatique lisent ensuite leurs réglages
 * comme d'habitude. Bouclier et réparation d'urgence, propres au joueur, sont retournés à part.
 */
import { slotIds, type EquipmentItem, type GameData, type ItemFamily, type ShipId, type SlotId } from '../data';

export type Loadout = Record<SlotId, string>;

export interface ShieldSpec {
  capacity: number;
  regenDelay: number;
  regenRate: number;
  breakDelay: number;
}

export interface EmergencyRepairSpec {
  threshold: number;
  amount: number;
  charges: number;
}

export interface EquipmentEffects {
  shield: ShieldSpec | null;
  emergencyRepair: EmergencyRepairSpec | null;
  /** Module de saut entre zones (chasseurs lourds). */
  jumpDrive: { charge: number } | null;
}

/** Famille d'objets acceptée par un emplacement : les armes suivent le châssis (HYDRA : missiles puis nuée). */
export function slotFamily(data: GameData, ship: ShipId, slot: SlotId): ItemFamily {
  const profile = data.ships.ships[ship];
  if (slot === 'primary') return profile.primary;
  if (slot === 'secondary') return profile.secondary;
  return slot;
}

export function itemFits(data: GameData, ship: ShipId, slot: SlotId, id: string): boolean {
  const item = data.equipment.items[id];
  return !!item && item.family === slotFamily(data, ship, slot) && (!item.chassis || item.chassis.includes(ship));
}

/** Emplacement proposé pour ce châssis : le saut n'apparaît que s'il existe un module compatible. */
export function slotAvailable(data: GameData, ship: ShipId, slot: SlotId): boolean {
  return slot !== 'jump' || compatibleItems(data, ship, slot).length > 1;
}

/** Objets montables sur un emplacement, du moins cher au plus cher. */
export function compatibleItems(data: GameData, ship: ShipId, slot: SlotId): [string, EquipmentItem][] {
  return Object.entries(data.equipment.items)
    .filter(([id]) => itemFits(data, ship, slot, id))
    .sort((a, b) => a[1].price - b[1].price);
}

export function defaultLoadout(data: GameData, ship: ShipId): Loadout {
  return { ...data.equipment.defaults[ship] };
}

/** Chargement valide : chaque emplacement porte un objet compatible, sinon l'objet par défaut. */
export function sanitizeLoadout(data: GameData, ship: ShipId, loadout: Partial<Record<string, string>> | undefined): Loadout {
  const out = defaultLoadout(data, ship);
  for (const slot of slotIds) {
    const id = loadout?.[slot];
    if (id && itemFits(data, ship, slot, id)) out[slot] = id;
  }
  return out;
}

/** Réglages effectifs d'une session équipée ; `base` n'est pas modifié. */
export function applyLoadout(base: GameData, ship: ShipId, requested: Partial<Loadout>): { data: GameData; loadout: Loadout; equipment: EquipmentEffects } {
  const loadout = sanitizeLoadout(base, ship, requested);
  const data = structuredClone(base);
  const p = data.ships.ships[ship];
  const equipment: EquipmentEffects = { shield: null, emergencyRepair: null, jumpDrive: null };
  let missileScale = 1, missileAdd = 0;
  for (const slot of slotIds) {
    const e = data.equipment.items[loadout[slot]]!.effects;
    if (e.laser && p.laser) p.laser = structuredClone(e.laser);
    if (e.missileCapacityScale) missileScale *= e.missileCapacityScale;
    if (e.missileCapacityAdd) missileAdd += e.missileCapacityAdd;
    if (e.missileDamageScale) data.weapons.missile.damage *= e.missileDamageScale;
    if (e.missileRadiusScale) data.weapons.missile.radius *= e.missileRadiusScale;
    if (e.swarmBurst) p.swarm.burst = e.swarmBurst;
    if (e.swarmDamageScale) data.weapons.swarm.damage *= e.swarmDamageScale;
    if (e.swarmEmission) data.weapons.swarm.emission = e.swarmEmission;
    if (e.shield) equipment.shield = { ...e.shield };
    if (e.speedScale) {
      p.cruiseMaxSpeed *= e.speedScale;
      p.boostMaxSpeed *= e.speedScale;
      p.reverseMaxSpeed *= e.speedScale;
    }
    if (e.accelerationScale) {
      p.cruiseAcceleration *= e.accelerationScale;
      p.boostAcceleration *= e.accelerationScale;
      p.reverseAcceleration *= e.accelerationScale;
    }
    if (e.boostCapacityScale) data.ships.flight.boostEnergy.capacity *= e.boostCapacityScale;
    if (e.boostRegenScale) data.ships.flight.boostEnergy.regen *= e.boostRegenScale;
    if (e.healthAdd) p.health = Math.max(10, p.health + e.healthAdd);
    if (e.agilityScale) {
      p.yawScale = Math.min(2, p.yawScale * e.agilityScale);
      p.pitchScale = Math.min(2, p.pitchScale * e.agilityScale);
      p.rollScale = Math.min(2, p.rollScale * e.agilityScale);
      p.lateralThrustScale = Math.min(1.5, p.lateralThrustScale * e.agilityScale);
    }
    if (e.emergencyRepair) equipment.emergencyRepair = { ...e.emergencyRepair };
    if (e.radarRangeScale) {
      data.hud.radarRange *= e.radarRangeScale;
      data.hud.markerRange *= e.radarRangeScale;
    }
    if (e.laserCoolScale) data.weapons.laserHeat.coolRate *= e.laserCoolScale;
    if (e.jumpDrive) equipment.jumpDrive = { ...e.jumpDrive };
  }
  if (p.missiles.max > 0 && (missileScale !== 1 || missileAdd !== 0)) {
    const scaled = (n: number) => Math.max(data.equipment.minMissiles, Math.round(n * missileScale) + missileAdd);
    p.missiles = { initial: scaled(p.missiles.initial), max: scaled(p.missiles.max) };
  }
  return { data, loadout, equipment };
}
